/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import msdyn365Commerce, * as Msdyn365 from '@msdyn365-commerce/core';
import axios from 'axios';
import {
    generateEmailContactPayload,
    getEmarsysHeader,
    EMARSYS_API_BASE_URL,
    generateNotifyErrorResponse,
    generateSuccessResponse,
    genterateWishlistPayload
} from './utilities/emarsys-utils';
export class PostNotifyMeInput implements Msdyn365.IActionInput {
    constructor() {}
    public getCacheKey = () => `PostNotifyMeInputCacheKey`;
    public getCacheObjectType = () => 'PostNotifyMeInputEventObjectType';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}
export interface IPostNotifyMeData {
    Result: string;
    Message: string;
    ErrorMessage: string;
}

const createInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    return new PostNotifyMeInput();
};

const getLogger = (cittaInstrumentationKey: string): any => {
    if (process.env.CURRENT_ENVIRONMENT !== 'web' && cittaInstrumentationKey) {
        const appInsights = require('applicationinsights');
        return new appInsights.TelemetryClient(cittaInstrumentationKey);
    }
    return null
}

async function action(input: PostNotifyMeInput, ctx: Msdyn365.IActionContext): Promise<IPostNotifyMeData> {
    let telemetryClient = getLogger(ctx.requestContext.app.config.cittaInstrumentationKey);
    const logMessage = (message: string) => {
        if (telemetryClient) {
          telemetryClient.trackTrace({ message: message });
        } else {
          console.log(message);
        }
    }

    const postBody = ctx.requestContext.postBody;
    const contentObject = postBody?.content;
    const bodyObject = JSON.parse(contentObject!.toString());

    if (ctx.requestContext.query?.failServerSide === 'true') {
        if (!msdyn365Commerce.isBrowser) {
            throw new Error('Thrown action');
        } else {
            logMessage('Post notify email: server side fail 1');
            return generateNotifyErrorResponse('Post notify email: server side fail 1');
        }
    } else if (ctx.requestContext.query?.returnMockData === 'true') {
        logMessage('Post notify email: server side fail 2');
        return generateNotifyErrorResponse('Post notify email: server side fail 2');
    }

    try {
        const usernameKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPIUsernameAU' : 'EmarsysAPIUsernameNZv2';
        const secretKeyvaultName = bodyObject?.country === 'AU' ? 'EmarsysAPISecretAU' : 'EmarsysAPISecretNZv2';
        const user = await msdyn365Commerce.secretManager?.getSecretValue(usernameKeyvaultName, ctx.requestContext.apiSettings.baseUrl);
        const secret = await msdyn365Commerce.secretManager?.getSecretValue(secretKeyvaultName, ctx.requestContext.apiSettings.baseUrl);

        if (!user?.value || !secret?.value) {
            const errors = {
                usernameError: user?.error?.message,
                secretError: user?.error?.message
            };

            logMessage(`Post notify email: unable to get keyvault values ${JSON.stringify(errors)}`);
            return generateNotifyErrorResponse(`Post notify email: unable to get keyvault values ${JSON.stringify(errors)}`);
        }

        const headers = getEmarsysHeader(user?.value || '', secret?.value || '');

        let emarsysPostBody = generateEmailContactPayload(bodyObject?.emailAddress, bodyObject?.firstName, bodyObject?.lastName, bodyObject?.phone, bodyObject?.postCode);
        if (bodyObject?.optIn) {
            emarsysPostBody[bodyObject.emarsysGroupId] = '1';
        }
        if (bodyObject?.emarsysSkuField && bodyObject?.itemId) {
            emarsysPostBody[bodyObject.emarsysSkuField] = bodyObject?.itemId;
        }
        
        const contactResponse = await axios.put(`${EMARSYS_API_BASE_URL}/api/v2/contact/?create_if_not_exists=1`,
            emarsysPostBody,
            {
                headers: headers
            })
            .then((response) => {
                logMessage(`Success message: ${JSON.stringify(response.data)}`);
                return generateSuccessResponse(`Success message: ${JSON.stringify(response.data)}`);
            })
            .catch((error) => {
                const errToReturn = error && error.response ? error.response.data : error
                logMessage(`Post Emarsys Contact: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
                return generateNotifyErrorResponse(`Post Emarsys Contact: catch at post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
            });

            if (contactResponse.Result === 'Success') {
                const wishlistPayload = genterateWishlistPayload(bodyObject?.emailAddress, bodyObject?.itemId);
                return await axios.post(`${EMARSYS_API_BASE_URL}/api/v2/wishlist/update`,
                    wishlistPayload,
                    {
                        headers: headers
                    })
                    .then((eventResponse) => {
                        logMessage(`Wishlist success - ${contactResponse.ErrorMessage} | ${JSON.stringify(eventResponse.data)}`);
                        return generateSuccessResponse(`Wishlist success - ${contactResponse.ErrorMessage} | ${JSON.stringify(eventResponse.data)}`);
                    })
                    .catch((error) => {
                        const errToReturn = error && error.response ? error.response.data : error;
                        logMessage(`Post Emarsys Contact: catch at post ${JSON.stringify(wishlistPayload)} ERROR: ${JSON.stringify(errToReturn)}`);
                        return generateNotifyErrorResponse(`Post Emarsys Contact: catch at wishlist post ${JSON.stringify(emarsysPostBody)} ERROR: ${JSON.stringify(errToReturn)}`);
                    });

            }
            else {
                return contactResponse;
            }

    } catch (e) {
        logMessage(`Post Emarsys Contact: catch ${JSON.stringify(e)}`);
        return generateNotifyErrorResponse(`Post Emarsys Contact: catch ${JSON.stringify(e)}`);
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IPostNotifyMeData>>action,
    // TODO: Give your data action a unique id
    id: 'PostNotifyMe',
    input: createInput
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
